// lib/api/notice.ts
import axiosInstance from './axios';
import { API_ENDPOINTS } from './config';
import { ApiError } from './error';

// 공지사항 타입 정의
export interface Notice {
  id: number;
  title: string;
  content: string;
  author: string;
  isFixed: boolean;
  viewCount: number;
  createdAt: string;
  updatedAt?: string;
  attachments?: NoticeAttachment[];
}

export interface NoticeAttachment {
  id: number;
  fileName: string;
  fileSize: number;
  downloadCount: number;
  createdAt: string;
}

export interface NoticeCreateRequest {
  title: string;
  content: string;
  author: string;
  isFixed?: boolean;
  createdBy?: number;
}

export interface NoticeUpdateRequest {
  title: string;
  content: string;
  author: string;
  isFixed?: boolean;
  updatedBy?: number;
}

export interface NoticeSearchParams {
  title?: string;
  author?: string;
  keyword?: string;
  startDate?: string;
  endDate?: string;
  isFixed?: boolean;
  page?: number;
  size?: number;
  sort?: string;
  direction?: 'ASC' | 'DESC';
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * 공지사항 목록 조회
 */
export async function getNoticesApi(params?: NoticeSearchParams): Promise<PageResponse<Notice>> {
  try {
    const data = await axiosInstance.get<PageResponse<Notice>>(
      API_ENDPOINTS.NOTICE.LIST,
      { params }
    );
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 공지사항 상세 조회
 */
export async function getNoticeApi(id: number): Promise<Notice> {
  try {
    const data = await axiosInstance.get<Notice>(
      API_ENDPOINTS.NOTICE.DETAIL(id)
    );
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 공지사항 등록
 */
export async function createNoticeApi(notice: NoticeCreateRequest, files?: FileList | File[]): Promise<Notice> {
  try {
    const formData = new FormData();
    
    // JSON 데이터를 Blob으로 변환하여 추가
    const noticeBlob = new Blob([JSON.stringify(notice)], {
      type: 'application/json',
    });
    formData.append('notice', noticeBlob);

    // 파일 추가
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });
    }

    const data = await axiosInstance.post<Notice>(
      API_ENDPOINTS.NOTICE.CREATE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 공지사항 수정
 */
export async function updateNoticeApi(id: number, notice: NoticeUpdateRequest, files?: FileList | File[]): Promise<Notice> {
  try {
    const formData = new FormData();
    
    // JSON 데이터를 Blob으로 변환하여 추가
    const noticeBlob = new Blob([JSON.stringify(notice)], {
      type: 'application/json',
    });
    formData.append('notice', noticeBlob);

    // 파일 추가
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });
    }

    const data = await axiosInstance.put<Notice>(
      API_ENDPOINTS.NOTICE.UPDATE(id),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 공지사항 삭제
 */
export async function deleteNoticeApi(id: number): Promise<void> {
  try {
    await axiosInstance.delete(API_ENDPOINTS.NOTICE.DELETE(id));
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 상단고정 설정/해제
 */
export async function toggleFixedApi(id: number): Promise<void> {
  try {
    await axiosInstance.patch(API_ENDPOINTS.NOTICE.TOGGLE_FIXED(id));
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 첨부파일 삭제
 */
export async function deleteAttachmentApi(attachmentId: number): Promise<void> {
  try {
    await axiosInstance.delete(API_ENDPOINTS.NOTICE.DELETE_ATTACHMENT(attachmentId));
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 첨부파일 다운로드 URL 생성
 */
export function getDownloadUrlApi(attachmentId: number): string {
  return `${import.meta.env.VITE_APP_API_URL}${API_ENDPOINTS.NOTICE.DOWNLOAD_ATTACHMENT(attachmentId)}`;
}

/**
 * 상단고정 공지사항 조회
 */
export async function getFixedNoticesApi(): Promise<Notice[]> {
  try {
    const data = await axiosInstance.get<Notice[]>(API_ENDPOINTS.NOTICE.FIXED_LIST);
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 최근 공지사항 조회
 */
export async function getRecentNoticesApi(limit = 5): Promise<Notice[]> {
  try {
    const data = await axiosInstance.get<Notice[]>(
      API_ENDPOINTS.NOTICE.RECENT_LIST,
      { params: { limit } }
    );
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 공지사항 통계
 */
export async function getNoticeStatsApi(): Promise<{
  totalCount: number;
  fixedCount: number;
  todayCount: number;
}> {
  try {
    const data = await axiosInstance.get<{
      totalCount: number;
      fixedCount: number;
      todayCount: number;
    }>(API_ENDPOINTS.NOTICE.STATS);
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}

/**
 * 이미지 업로드 (CKEditor용)
 */
export async function uploadImageApi(file: File): Promise<{ url: string }> {
  try {
    const formData = new FormData();
    formData.append('upload', file);

    const data = await axiosInstance.post<{ url: string }>(
      API_ENDPOINTS.NOTICE.UPLOAD_IMAGE,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  } catch (error) {
    throw ApiError.fromAxiosError(error);
  }
}